import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer, useLocalObservable } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import { Role } from '@feathr/blackbox';
import { ButtonValid, CreatableSelect, Form, Select, toast, Tooltip } from '@feathr/components';
import Page from '@feathr/extender/App/Page';
import { StoresContext, useAccount, useLocalUrl } from '@feathr/extender/state';
import { flattenError, flattenErrors, logUserEvents } from '@feathr/hooks';
import type { IRachisMessage } from '@feathr/rachis';
import { validate, wretch } from '@feathr/rachis';

interface IUserOption {
  label: string;
  value: string;
}

interface ICreateUserOption extends IUserOption {
  create: boolean;
}

interface IRoleOption {
  id: Role;
  name: string;
}

function InviteUsersPage(): JSX.Element {
  const { Users, Accounts } = useContext(StoresContext);
  const account = useAccount();
  const history = useHistory();
  const localStore = useLocalObservable<{ role: Role; addresses: string[] }>(() => ({
    role: Role.normie,
    addresses: [],
  }));
  const localUrl = useLocalUrl();
  const { t } = useTranslation();

  // TODO: Move this function to users.ts.
  async function handleInvite(): Promise<void> {
    const fetches = localStore.addresses.map(async (email) => {
      return await wretch<IRachisMessage>(`${BLACKBOX_URL}invite`, {
        method: 'POST',
        body: JSON.stringify({ email, role: localStore.role }),
        headers: Users.getHeaders(),
      });
    });
    // TODO: Add error handling.
    await Promise.all(fetches);
    Users.refreshApiCache();
    if (!!account) {
      Accounts.remove(account.id);
    }
    logUserEvents({
      'Invited user': {
        invited_users_emails: [...localStore.addresses],
      },
    });
    toast(`Sent invite links to ${localStore.addresses.length} users`);
    history.push(localUrl('/settings/account/users'));
  }

  const roleOptions = [
    {
      id: Role.admin,
      name: t('Admin'),
    },
    {
      id: Role.normie,
      name: t('User'),
    },
  ];
  const roleValue = localStore.role ? roleOptions[0] : roleOptions[1];

  function validateForm(): Record<'emails', string[]> {
    return (
      validate(
        // Array validator requires an array of objects
        { emails: localStore.addresses.map((email) => ({ label: email, value: email })) },
        {
          emails: {
            array: {
              value: {
                presence: { allowEmpty: false },
                email: true,
              },
            },
            length: {
              minimum: 1,
              message: '^Please provide at least 1 email address.',
            },
          },
        },
      ) || {}
    );
  }

  function handleCreateOption(inputValue: string): ICreateUserOption {
    return { label: inputValue, value: inputValue, create: true };
  }

  function handleFilterOption(value: IUserOption): boolean {
    return value.label.startsWith('Add ');
  }

  function handleFormatCreateLabel(inputValue: string): string {
    return `Add ${inputValue}`;
  }

  function handleIsValidNewOption(inputValue: string): boolean {
    const isValid =
      !localStore.addresses.includes(inputValue) &&
      validate.validate({ inputValue }, { inputValue: { email: true } }) === undefined;
    return isValid;
  }

  function handleSelectMultipleUsers(newValues: IUserOption[]): void {
    localStore.addresses = newValues.map(({ value }) => value);
  }

  function handleSelectRole({ id }: IRoleOption): void {
    localStore.role = id;
  }

  function noUserOptionsMessage({ inputValue }: { inputValue: string }): string {
    if (inputValue.length === 0) {
      return t('Input a valid email address.');
    }
    return t('Invalid email address.');
  }

  const userHelpText = (
    <Trans t={t}>
      Enter one or more email addresses.
      <br />
      After each address hit Enter/Return to start the next one.
    </Trans>
  );

  const roleHelpText = (
    <Trans t={t}>
      <span>
        Newly invited users will be assigned the selected role.{' '}
        <Tooltip
          title={
            <p>
              <strong>Users</strong> can access any project.
              <br />
              <strong>Admins</strong> can access any project, invite new users, and change user
              roles.
            </p>
          }
        >
          <FontAwesomeIcon icon={faInfoCircle} />
        </Tooltip>
      </span>
    </Trans>
  );

  const errors = validateForm();

  return (
    <Page title={t('Invite Users')}>
      <Form
        actions={[
          <ButtonValid
            errors={flattenErrors(errors)}
            key={'invite'}
            name={'invite-users-button'}
            onClick={handleInvite}
            type={'primary'}
          >
            {t('Invite {{count}} user', { count: localStore.addresses.length })}
          </ButtonValid>,
        ]}
        label={t('Invite users')}
      >
        <CreatableSelect
          backspaceRemovesValue={true}
          components={{
            // Null to disable the components.
            DropdownIndicator: (): null => null,
            IndicatorSeparator: (): null => null,
            Menu: (): null => null,
          }}
          createOption={handleCreateOption}
          defaultOptions={[] as Array<{ value: string; label: string }>}
          filterOption={handleFilterOption}
          formatCreateLabel={handleFormatCreateLabel}
          helpText={userHelpText}
          isClearable={true}
          isMulti={true}
          isValidNewOption={handleIsValidNewOption}
          label={t('Email addresses')}
          noOptionsMessage={noUserOptionsMessage}
          onSelectMulti={handleSelectMultipleUsers}
          placeholder={t('Input an email address...')}
          validationError={localStore.addresses.length ? flattenError(errors.emails) : ''}
          value={localStore.addresses.map((email) => ({ label: email, value: email }))}
        />
        <Select
          helpText={roleHelpText}
          label={t('Role')}
          onSelectSingle={handleSelectRole}
          options={roleOptions}
          value={roleValue}
        />
      </Form>
    </Page>
  );
}

export default observer(InviteUsersPage);
